import { useLocation, useNavigate } from 'react-router-dom';
import Loading from '../Status/Loading';
import { useKeycloak } from 'src/components/AuthHOC';
import { IReviewerInvitationAction } from '@publishing-engine/domain/types';
import { getReviewRequestContextFromRoute } from 'src/contexts/ReviewDataProvider';
import { useQuery } from '@tanstack/react-query';
import { fetchReview, fetchSubmissionContext } from 'src/api/queries';
import NotFound from '../Status/NotFound';
import {
  useAcceptReviewRequest,
  useCoReviewInvitationDecision
} from 'src/api/commands';
import { useToast } from '@morressier/alembic';
import { useEffect } from 'react';

export const ReviewerInvitation = () => {
  const toast = useToast();
  const { currentUser } = useKeycloak();
  const location = useLocation();
  const navigate = useNavigate();

  const action = new URLSearchParams(location.search).get(
    'action'
  ) as IReviewerInvitationAction;
  const userId = new URLSearchParams(location.search).get('userId');

  useEffect(() => {
    if (currentUser.id !== userId) {
      toast({
        variant: 'attention',
        title: 'Invalid Email Link',
        description:
          'This link you are trying to access requires a different login. Please log out and sign in to the correct account to continue.',
        isClosable: true
      });

      navigate('/app/reviews');
    }
  }, [currentUser, userId]);

  const { reviewId } = getReviewRequestContextFromRoute();

  const queryKey = ['review', { id: reviewId }];
  const {
    data: review = {
      submissionId: '',
      id: '',
      submissionContextId: '',
      caseData: { submission: { id: '' } }
    },
    isLoading: isLoadingReview
  } = useQuery({
    queryKey,
    queryFn: () => fetchReview(reviewId),
    retry: 0
  });

  const {
    data: submissionContext = {
      organizationId: ''
    },
    isLoading: isLoadingSubmissionContext
  } = useQuery({
    queryKey: ['submission-context', review.submissionContextId],
    queryFn: () => fetchSubmissionContext(review.submissionContextId),
    enabled: !!review.submissionContextId
  });

  const acceptReviewRequestMutation = useAcceptReviewRequest({
    organizationId: submissionContext?.organizationId,
    submissionId: review.caseData.submission.id,
    reviewId: review.id,
    submissionContextId: review.submissionContextId,
    queryKey: []
  });

  const acceptCoReviewInvitationMutation = useCoReviewInvitationDecision({
    reviewId: review.id,
    decision: 'acceptRequest',
    queryKey
  });

  if (isLoadingReview || isLoadingSubmissionContext) return <Loading />;
  if (!review) return <NotFound />;

  if (!action) {
    navigate(`/app/reviews/${review.id}`);
    return <></>;
  }

  const acceptReview = () => {
    acceptReviewRequestMutation.mutate();
  };

  switch (action) {
    case 'acceptFromEmail': {
      // Reviewer Accepts the invitation without Co-Review option
      acceptReview();
      navigate(`/app/reviews/${review.id}`);
      break;
    }
    case 'acceptWithCoReviewFromEmail': {
      // Reviewer Accepts the invitation with Co-Review option
      acceptReview();
      navigate(`/app/reviews/${review.id}/coreview-invite`);
      break;
    }
    case 'acceptCoReviewFromEmail': {
      // Co-Reviewer accepts the invitation
      acceptCoReviewInvitationMutation.mutate();
      navigate(`/app/reviews/${review.id}`);
      break;
    }
  }

  return <Loading />;
};
